import React, { useCallback } from 'react';

import { Paragraph, Button, useColorModeValue } from '@vimeo/bokeh';
import { Checkmark } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { trackVoteInPoll } from '@vimeo/live-core/tracking/tracking_engagement_polls';
import { IPoll } from '@vimeo/live-core/types/interaction_tools';

import { createInteractionDomName } from '../../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../../translations';
import { PollOptionVotesBarHidden } from '../../../polls/poll_item/PollOptionVotesBarHidden';

interface IPollVoteProps extends IBaseDomElementProps {
  poll: IPoll;
  isVotedOption: boolean;
  onSetVote(id: string, index: number): void;
}

export function PollVote({
  id = createInteractionDomName('poll-vote'),
  poll,
  isVotedOption,
  onSetVote,
}: IPollVoteProps) {
  const isVoted: boolean = poll.options.some((it) => it.isVotedByMe);

  const buttonBorderColor = useColorModeValue('slate.400', 'grayscale.700');
  const buttonVotedBackgroundColor = useColorModeValue('slate.400', 'grayscale.600');
  const buttonHoverBackgroundColor = useColorModeValue('slate.50', 'grayscale.700');

  const onVoteForPoll = useCallback(
    (pollId: string, voteIndex: number) => {
      onSetVote(pollId, voteIndex);
      trackVoteInPoll();
    },
    [onSetVote],
  );

  if (isVoted) {
    return (
      <>
        {poll.options.map((it, index) => (
          <PollOptionVotesBarHidden
            id={createInteractionDomName(id, 'option', index)}
            key={it.id}
            option={it}
            isBold={true}
          />
        ))}

        <Paragraph size={'sm'} color={'text-secondary'}>
          {translations.waitingForFinalResults}
        </Paragraph>
      </>
    );
  }

  return (
    <>
      {poll.options.map((it, index) => (
        <Button
          key={it.id}
          id={createInteractionDomName(id, 'option', index)}
          aria-label={it.text}
          variant={'minimal'}
          rightIcon={it.isVotedByMe ? <Checkmark /> : undefined}
          isDisabled={isVotedOption}
          width={'100%'}
          justifyContent={'space-between'}
          flexShrink={0}
          border={'1px solid'}
          whiteSpace={'break-spaces'}
          textAlign={'left'}
          minHeight={'sm'}
          height={'auto'}
          borderColor={buttonBorderColor}
          backgroundColor={`${
            it.isVotedByMe ? buttonVotedBackgroundColor : 'transparent'
          }!important`}
          _disabled={{
            opacity: 1,
          }}
          _hover={{
            backgroundColor: `${buttonHoverBackgroundColor}!important`,
          }}
          onClick={() => onVoteForPoll(poll.id, index)}
        >
          {it.text}
        </Button>
      ))}
    </>
  );
}
